import { UnisphereRuntimeLoader, UnisphereRuntimeVisual } from "@unisphere/runtime-react";
import { useContext, useMemo } from "react";
import { useEventHandler } from "@kaltura/mediaspace-shared-hooks";
import { ConfigContext } from "@kaltura/mediaspace-shared-data-kms-config/ConfigContext";
import { useConfig } from "@kaltura/mediaspace-shared-data-kms-config/useConfig";
import { version } from "@kaltura/mediaspace-shared-data-kms-version";
import { ChatRuntimeName, ChatRuntimeSettings, WidgetName } from "@unisphere/genie-core";
import { widgetName as PlgWidgetName, WidgetsRuntimeName } from "@kaltura-corp/unisphere-plg-types";
import { styled } from "@mui/material";
import { Box } from "@kaltura/mediaspace-shared-styled";
import { headerHeight } from "@kaltura/mediaspace-shared-constants";
import { sidebarButtonsSize, useIsMobileSidebarLayout, useSidebarLayoutContext } from "@kaltura/ds-react-layouts";
import { buildBaseGenieSettings, getGenieContext, getCategoryUrl } from "./helper";
import { usePlgWidgetSettings } from "./usePlgWidgetSettings";
import { BaseGenieProps } from "./types";

export interface GenieProps extends BaseGenieProps {
    genieServerUrl: string;
    uiconfId: string;
    isFullSize?: boolean;
    onExpand?: () => void;
    onClose?: () => void;
    isExpanded?: boolean;
}

/*
 * Genie widget
 * renders a Genie chat widget
 * used in ds pages
 */
export const Genie = ({
    genieServerUrl,
    ks,
    uiconfId,
    categoryData,
    isAncestorType = false,
    isFullSize = false,
    agentMode = false,
    entryId,
    visualType = "page",
    onExpand,
    onClose,
    isExpanded = false,
}: GenieProps) => {
    const {
        application: { partnerId, serviceUrl },
        analytics,
    } = useConfig();

    const configContext = useContext(ConfigContext);
    const headerColor = configContext.dsLayout?.headerColor;
    const topSpacing = headerColor && headerColor !== "transparent" ? headerHeight : 0;

    const { isEnabled } = useSidebarLayoutContext();
    const isMobileSidebarLayout = useIsMobileSidebarLayout();
    const bottomSpacing = isEnabled && isMobileSidebarLayout ? sidebarButtonsSize : 0;

    // Calculate minHeight style for full size mode container
    const minHeight = isFullSize ? `calc(100vh - ${topSpacing + bottomSpacing}px)` : undefined;

    const categoryUrl = getCategoryUrl(categoryData);

    const settings = useMemo<ChatRuntimeSettings>(
        () => ({
            ...buildBaseGenieSettings({ ks, partnerId, agentMode, categoryData, categoryUrl }),
            genieServerUrl,
            kalturaServerURI: serviceUrl,
            uiconfId,
            analytics: {
                enabled: true,
                serverUrl: "https://" + analytics.serviceUrl,
                appName: analytics.appName,
                appVersion: version,
            },
        }),
        [
            analytics.appName,
            analytics.serviceUrl,
            categoryData,
            categoryUrl,
            genieServerUrl,
            ks,
            partnerId,
            serviceUrl,
            uiconfId,
            agentMode,
        ]
    );

    const stableOnClose = useEventHandler(onClose);
    const stableOnExpand = useEventHandler(onExpand);

    const visualSettings = useMemo(() => {
        if (!onClose && !onExpand) {
            return null;
        }
        const context = getGenieContext({ entryId, categoryData, isAncestorType });
        return {
            scroll: isExpanded ? undefined : "container",
            onRequestClose: stableOnClose,
            onExpand: stableOnExpand,
            isExpanded,
            closeButtonType: "drawer",
            ...(context ? { context } : {}),
        };
        // !! because stableOnClose/stableOnExpand are always defined (useEventHandler always returns a wrapper),
        // so we track presence of the original props as booleans to avoid re-running on every render
        // eslint-disable-next-line react-hooks/exhaustive-deps,react-hooks/use-memo
    }, [!!onClose, !!onExpand, stableOnClose, stableOnExpand, entryId, categoryData, isAncestorType, isExpanded]);

    const plgWidgetSettings = usePlgWidgetSettings(ks);

    return (
        <StyledOuterWrapper isFullSize={isFullSize} minHeight={minHeight}>
            <UnisphereRuntimeLoader
                widgetName={WidgetName}
                runtimeName={ChatRuntimeName}
                runtimeSettings={settings as unknown as Record<string, unknown>}
            />
            <UnisphereRuntimeLoader
                widgetName={PlgWidgetName}
                runtimeName={WidgetsRuntimeName}
                runtimeSettings={plgWidgetSettings as unknown as Record<string, unknown>}
            />
            <UnisphereRuntimeVisual
                visualSettings={visualSettings}
                visualType={visualType}
                widgetName={WidgetName}
                runtimeName={ChatRuntimeName}
            />
        </StyledOuterWrapper>
    );
};

const StyledOuterWrapper = styled(
    Box,
    {
        shouldForwardProp(propName) {
            return propName !== "isFullSize";
        },
    }
)(({ isFullSize, minHeight }: { isFullSize: boolean; minHeight?: string }) => [
    {
        position: "relative",
        width: "100%",
        height: "100%",
        minHeight,
    },
    isFullSize && {
        // full size mode - flex column to allow inner content to stretch
        display: "flex",
        flexDirection: "column",
        "& > div:first-of-type": {
            display: "flex",
            flex: "1",
        },
    },
]);
